/**
 * Time formatting utilities for display
 * Converts seconds since midnight to human-readable strings
 */

import { DURATION_STYLES, TIME_CONSTANTS } from "../constants";
import { secondsToTime12Hour } from "../core/time";
import { getTimezoneAbbreviation } from "../core/timezone";

/**
 * Format seconds since midnight as 12-hour time string
 * @param {number} seconds - Seconds since midnight
 * @returns {string} "9:30 AM", "12:00 PM", etc.
 */
export const formatTime12Hour = (seconds) => {
  const { hour, minute, period } = secondsToTime12Hour(seconds);
  return `${hour}:${minute.toString().padStart(2, "0")} ${period}`;
};

/**
 * Format seconds since midnight with timezone
 * @param {number} seconds - Seconds since midnight
 * @param {string} timezone - Timezone identifier
 * @returns {string} "9:30 AM ET", "12:00 PM UYT", etc.
 */
export const formatTimeWithTimezone = (seconds, timezone) => {
  const baseTime = formatTime12Hour(seconds);
  if (timezone) {
    const abbreviation = getTimezoneAbbreviation(timezone);
    return `${baseTime} ${abbreviation}`;
  }
  return baseTime;
};

/**
 * Format time range
 * @param {number} startSeconds - Start time in seconds since midnight
 * @param {number} endSeconds - End time in seconds since midnight
 * @param {boolean} [includeTimezone=false] - Whether to include timezone
 * @param {string} [timezone] - Timezone identifier
 * @returns {string} "9:30 AM - 5:30 PM", "11:00 PM - 2:00 AM", etc.
 */
export const formatTimeRange = (
  startSeconds,
  endSeconds,
  includeTimezone = false,
  timezone = null
) => {
  const startStr =
    includeTimezone && timezone
      ? formatTimeWithTimezone(startSeconds, timezone)
      : formatTime12Hour(startSeconds);
  const endStr = formatTime12Hour(endSeconds);

  // Add next day indicator for overnight shifts
  const isOvernight = endSeconds >= TIME_CONSTANTS.SECONDS_PER_DAY;
  const endDisplay = isOvernight ? `${endStr} (next day)` : endStr;
  return `${startStr} - ${endDisplay}`;
};

/**
 * Format duration in hours
 * @param seconds - Duration in seconds
 * @param style - Format style (DURATION_STYLES.SHORT for "8h 30m", DURATION_STYLES.LONG for "8 hours 30 minutes")
 * @param alwaysShowMinutes - If true, always show minutes even when 0 (default: false)
 * @returns "8h", "7.5h", "7h 30m", "8h 00m" (if alwaysShowMinutes), etc.
 */
export const formatDuration = (
  seconds,
  style = DURATION_STYLES.SHORT,
  alwaysShowMinutes = false
) => {
  const hours = Math.floor(seconds / TIME_CONSTANTS.SECONDS_PER_HOUR);
  const remainingSeconds = seconds % TIME_CONSTANTS.SECONDS_PER_HOUR;
  const minutes = Math.round(
    remainingSeconds / TIME_CONSTANTS.SECONDS_PER_MINUTE
  );
  if (style === DURATION_STYLES.SHORT) {
    if (minutes === 0 && !alwaysShowMinutes) {
      return `${hours}h`;
    }
    if (hours === 0) {
      return `${minutes}m`;
    }
    // Pad minutes to 2 digits for consistent alignment
    const minutesStr = minutes.toString().padStart(2, "0");
    return `${hours}h ${minutesStr}m`;
  }

  // Long style
  if (minutes === 0 && !alwaysShowMinutes) {
    return hours === 1 ? "1 hour" : `${hours} hours`;
  }
  if (hours === 0) {
    return minutes === 1 ? "1 minute" : `${minutes} minutes`;
  }
  const hourStr = hours === 1 ? "1 hour" : `${hours} hours`;
  const minStr = minutes === 1 ? "1 minute" : `${minutes} minutes`;
  return `${hourStr} ${minStr}`;
};

/**
 * Format duration as decimal hours
 * @param seconds - Duration in seconds
 * @returns "8.00", "7.50", "7.25", etc.
 */
export const formatDecimalHours = (seconds, decimalPlaces = 2) => {
  const hours = seconds / TIME_CONSTANTS.SECONDS_PER_HOUR;
  return hours.toFixed(decimalPlaces);
};

/**
 * Format break time
 * Special formatting for breaks within shifts
 * @param {number} breakStartSeconds - Break start time in seconds since midnight
 * @param {number} breakEndSeconds - Break end time in seconds since midnight
 * @returns {string} "12:00 PM - 12:30 PM (30m)"
 */
export const formatBreakTime = (breakStartSeconds, breakEndSeconds) => {
  const duration = breakEndSeconds - breakStartSeconds;
  const durationStr = formatDuration(duration, DURATION_STYLES.SHORT);
  return `${formatTime12Hour(breakStartSeconds)} - ${formatTime12Hour(breakEndSeconds)} (${durationStr})`;
};

/**
 * Get display time for overnight shifts
 * Shows next day indicator when appropriate
 * @param {number} seconds - Time in seconds since midnight
 * @returns {Object} Object with time string and day indicator
 */
export const getOvernightDisplay = (seconds) => {
  const isNextDay = seconds >= TIME_CONSTANTS.SECONDS_PER_DAY;
  const timeStr = formatTime12Hour(seconds);
  return {
    time: timeStr,
    isNextDay,
    dayIndicator: isNextDay ? "+1 day" : undefined,
  };
};

/**
 * Format relative time
 * @param {number} timeSeconds - Time in seconds since midnight
 * @param {number} currentTimeSeconds - Current time in seconds since midnight
 * @returns {string} "in 2 hours", "3 hours ago", etc.
 */
export const formatRelativeTime = (timeSeconds, currentTimeSeconds) => {
  const diffSeconds = timeSeconds - currentTimeSeconds;
  const absSeconds = Math.abs(diffSeconds);

  if (absSeconds < TIME_CONSTANTS.SECONDS_PER_MINUTE) {
    return "now";
  }

  const duration = formatDuration(absSeconds, DURATION_STYLES.LONG);
  return diffSeconds > 0 ? `in ${duration}` : `${duration} ago`;
};

/**
 * Format duration from decimal hours
 * Convenience wrapper for formatDuration that accepts decimal hours
 * @param decimalHours - Hours as a decimal number (e.g., 8.5)
 * @param style - Format style (DURATION_STYLES.SHORT for "8h 30m", DURATION_STYLES.LONG for "8 hours 30 minutes")
 * @param alwaysShowMinutes - If true, always show minutes even when 0 (default: false)
 * @returns Formatted duration string
 */
export const formatDurationFromHours = (
  decimalHours,
  style = DURATION_STYLES.SHORT,
  alwaysShowMinutes = false
) => {
  const seconds = decimalHours * TIME_CONSTANTS.SECONDS_PER_HOUR;
  return formatDuration(seconds, style, alwaysShowMinutes);
};

/**
 * Check if shift has enough data to display meaningful totals
 * @param {ShiftData} shift - Shift object
 * @returns {boolean} True if both start and end times are set
 */
export const hasDisplayableShiftData = (shift) => {
  return shift.startTime !== null && shift.endTime !== null;
};

/**
 * Transform raw GraphQL timezone data into select options for dropdowns
 * @param {Array} timezones - Array of timezone objects with tzname, display_name, utc_offset
 * @returns {Array} Array of select options with value and text properties
 */
export const transformTimezonesToSelectOptions = (timezones) => {
  const seen = new Set();
  return timezones
    .filter((tz) => {
      if (seen.has(tz.tzname)) return false;
      seen.add(tz.tzname);
      return true;
    })
    .map((tz) => ({
      value: tz.tzname,
      text: `${tz.display_name} (${tz.utc_offset})`,
    }));
};
